import {
  getObject,
  getList,
  LeoUUID,
  getLeoUuid,
  getInt32,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class AssignSpocsToRespondentsRPC implements
  LeoRPC<AssignSpocsToRespondentsRPC.Request, AssignSpocsToRespondentsRPC.Response, AssignSpocsToRespondentsRPC.Errors.Errors>
{
  abstract execute(
    request: AssignSpocsToRespondentsRPC.Request
  ): Promise<LeoRPCResult<AssignSpocsToRespondentsRPC.Response, AssignSpocsToRespondentsRPC.Errors.Errors>>;
}

export namespace AssignSpocsToRespondentsRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    respondentSelection: RespondentSelection.RespondentSelectionDTO;
    spocIds: string[];
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAssignSpocsToRespondentsRPCRequest: boolean = true;
  
    readonly respondentSelection: RespondentSelection.RespondentSelection;
    readonly spocIds: LeoUUID[];
    readonly projectId: LeoUUID;
  
  
    constructor(
    respondentSelection: RespondentSelection.RespondentSelection, 
    spocIds: LeoUUID[], 
    projectId: LeoUUID
    ) {
      this.respondentSelection = respondentSelection;
      this.spocIds = spocIds;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const respondentSelection = RespondentSelection.fromDTO(getObject(dto, "respondentSelection", InvalidRequestRespondentSelectionError));
      const spocIds = getList<string, LeoUUID>(
        dto,
        "spocIds",
        (e: string) => new LeoUUID(e),
        InvalidRequestSpocIdsError
      );
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        respondentSelection,
        spocIds,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          respondentSelection: this.respondentSelection.toDTO(),
          spocIds: this.spocIds.map((e: LeoUUID) => { return e.toDTO(); }),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AssignSpocsToRespondentsRPC(respondentSelection='${this.respondentSelection}',spocIds='${this.spocIds}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    totalAssignedRespondents: number;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isAssignSpocsToRespondentsResponse: boolean = true;
  
    readonly totalAssignedRespondents: number;
  
  
    constructor(
    totalAssignedRespondents: number
    ) {
      this.totalAssignedRespondents = totalAssignedRespondents;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const totalAssignedRespondents = getInt32(dto, "totalAssignedRespondents", InvalidResponseTotalAssignedRespondentsError);
      return new Response(
        totalAssignedRespondents
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          totalAssignedRespondents: this.totalAssignedRespondents
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `AssignSpocsToRespondents(totalAssignedRespondents='${this.totalAssignedRespondents}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidSpocId = "INVALID_SPOC_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER",
    InvalidRespondentIdFound = "INVALID_RESPONDENT_ID_FOUND",
    MaximumRespondentSpocLimitReached = "MAXIMUM_RESPONDENT_SPOC_LIMIT_REACHED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AssignSpocsToRespondentsRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `AssignSpocsToRespondentsRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AssignSpocsToRespondentsRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `AssignSpocsToRespondentsRPC.DELETED_COLUMN_FILTER()`;
    };
    
    
    export interface InvalidRespondentIdFoundDTO {
      code: string;
    }
    
    export class InvalidRespondentIdFound extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentIdFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentIdFound;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdFoundDTO {
        const dto: InvalidRespondentIdFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentIdFound.prototype.toString = function toString(): string {
      return `AssignSpocsToRespondentsRPC.INVALID_RESPONDENT_ID_FOUND()`;
    };
    
    
    export interface MaximumRespondentSpocLimitReachedDTO {
      code: string;
    }
    
    export class MaximumRespondentSpocLimitReached extends LeoRPCError<RPCError> {
      readonly isMaximumRespondentSpocLimitReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumRespondentSpocLimitReached;
        super(code);
      }
    
    
      toDTO(): MaximumRespondentSpocLimitReachedDTO {
        const dto: MaximumRespondentSpocLimitReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumRespondentSpocLimitReached.prototype.toString = function toString(): string {
      return `AssignSpocsToRespondentsRPC.MAXIMUM_RESPONDENT_SPOC_LIMIT_REACHED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidSpocId | ProjectAlreadyArchived | DeletedColumnFilter | InvalidRespondentIdFound | MaximumRespondentSpocLimitReached;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidSpocIdDTO | ProjectAlreadyArchivedDTO | DeletedColumnFilterDTO | InvalidRespondentIdFoundDTO | MaximumRespondentSpocLimitReachedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        case RPCError.InvalidRespondentIdFound:
          return new InvalidRespondentIdFound();
        case RPCError.MaximumRespondentSpocLimitReached:
          return new MaximumRespondentSpocLimitReached();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestRespondentSelectionError extends AssignSpocsToRespondentsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentSelectionError";
  }
}

export class InvalidRequestSpocIdsError extends AssignSpocsToRespondentsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSpocIdsError";
  }
}

export class InvalidRequestProjectIdError extends AssignSpocsToRespondentsRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseTotalAssignedRespondentsError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseTotalAssignedRespondentsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidSpocIdError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

export class InvalidResponseInvalidRespondentIdFoundError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdFoundError";
  }
}

export class InvalidResponseMaximumRespondentSpocLimitReachedError extends AssignSpocsToRespondentsRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumRespondentSpocLimitReachedError";
  }
}

